/**
 * Error element for the order detail route (orders/:id).
 * Renders CenteredMessageWithAction based on errors thrown by the order loader:
 * - "Order ID is required" / "Invalid order ID" -> invalid request
 * - Response 403 -> unauthorized to view this order
 * - Response 404 -> not found with optional message
 * - Other errors -> generic message with error.message
 */
import {
  isRouteErrorResponse,
  NavigateFunction,
  useNavigate,
  useParams,
  useRouteError,
} from 'react-router-dom';
import { CenteredMessageWithAction } from 'src/components/centered_message_with_action';

function getOrderDetailErrorContent(
  error: unknown,
  navigate: NavigateFunction,
  organization?: string,
  orderId?: string,
): {
  title: string;
  description: string;
  buttonText: string;
  onButtonClick: () => void;
} {
  const defaultButtonText = 'Back to Orders';
  const defaultOnButtonClick = () => {
    navigate(organization ? `/${organization}/orders` : '/');
  };

  if (isRouteErrorResponse(error)) {
    const response = error;
    const descriptionFromData =
      typeof response.data === 'string'
        ? response.data
        : ((response.data as { message?: string })?.message ??
          'An error occurred. Please try again.');

    if (response.status === 400) {
      return {
        title: '400 - Bad request',
        description: descriptionFromData,
        buttonText: defaultButtonText,
        onButtonClick: () =>
          navigate(
            organization && orderId
              ? `/${organization}/orders/${orderId}`
              : '/',
          ),
      };
    } else if (response.status === 401) {
      return {
        title: '401 - Unauthorized',
        description: descriptionFromData,
        buttonText: defaultButtonText,
        onButtonClick: () =>
          navigate(
            organization && orderId
              ? `/${organization}/orders/${orderId}`
              : '/',
          ),
      };
    } else if (response.status === 403) {
      return {
        title: '403 - Forbidden',
        description: descriptionFromData,
        buttonText: defaultButtonText,
        onButtonClick: defaultOnButtonClick,
      };
    } else if (response.status === 404) {
      return {
        title: '404 - Not found',
        description: descriptionFromData,
        buttonText: defaultButtonText,
        onButtonClick: defaultOnButtonClick,
      };
    }
  }

  return {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again.',
    buttonText: defaultButtonText,
    onButtonClick: defaultOnButtonClick,
  };
}

export function OrderDetailErrorElement() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { organization, id: orderId } = useParams<{
    organization: string;
    id: string;
  }>();

  const { title, description, buttonText, onButtonClick } =
    getOrderDetailErrorContent(error, navigate, organization, orderId);

  return (
    <CenteredMessageWithAction
      title={title}
      description={description}
      buttonText={buttonText}
      onButtonClick={onButtonClick}
    />
  );
}
